import { useEffect, useMemo, useRef, useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Send, User, Headphones, Paperclip, X, Download, Play, UserCheck, UserX, ChevronDown, Mail, Package } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { AdminTicket, AdminTicketThread, AttachmentMeta, ReplyAttachment, TicketEvent, TicketStatus, TicketPriority, Product } from '@/types';

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: 'new',      label: 'New'      },
  { value: 'open',     label: 'Open'     },
  { value: 'pending',  label: 'Pending'  },
  { value: 'closed',   label: 'Closed'   },
  { value: 'resolved', label: 'Resolved' },
];

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: 'low',    label: 'Low'    },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High'   },
  { value: 'urgent', label: 'Urgent' },
];

const STATUS_BADGE: Record<string, string> = {
  new:      'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
  open:     'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  pending:  'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  closed:   'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  resolved: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
};

const PRIORITY_BADGE: Record<string, string> = {
  low:    'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  medium: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  high:   'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  urgent: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
};

const ALLOWED_MIME: Record<string, boolean> = {
  'image/png': true, 'image/jpeg': true, 'image/gif': true, 'image/webp': true,
  'application/pdf': true,
  'application/vnd.ms-excel': true,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': true,
  'application/msword': true,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': true,
};

function relativeTime(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d}d ago`;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s{2,}/g, ' ').trim();
}

/** True when a thread body should be rendered as HTML (e-mail originated) */
function looksLikeHtml(body: string): boolean {
  return /<[a-zA-Z][\s\S]*?>/.test(body);
}

// ─── HTML e-mail renderer ─────────────────────────────────────────────────────
// Renders arbitrary HTML e-mail bodies inside a sandboxed iframe so:
//   • The e-mail's own CSS / layout is preserved exactly as intended
//   • Scripts are blocked (no allow-scripts in sandbox)
//   • Links open in a new tab (allow-popups-to-escape-sandbox)
//   • The iframe auto-sizes to its content height

function HtmlEmailBody({ html }: { html: string }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(80);

  // Strip <script> blocks server-side safety net (sandbox already blocks execution)
  const safeHtml = useMemo(
    () => html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ''),
    [html],
  );

  // Inject a minimal override so the email doesn't break our layout,
  // then let the email's own <style> tags take full control.
  const srcDoc = useMemo(() => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <style>
    html, body { margin: 0 !important; padding: 0 !important; overflow-x: hidden; }
    img { max-width: 100%; height: auto; }
    * { box-sizing: border-box; }
  </style>
</head>
<body>${safeHtml}</body>
</html>`, [safeHtml]);

  function handleLoad() {
    try {
      const body = frameRef.current?.contentDocument?.body;
      if (body) setHeight(body.scrollHeight + 8);
    } catch { /* cross-origin — keep default height */ }
  }

  return (
    <iframe
      ref={frameRef}
      srcDoc={srcDoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      onLoad={handleLoad}
      title="Email content"
      style={{ width: '100%', height: `${height}px`, border: 'none', display: 'block' }}
    />
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Timeline ─────────────────────────────────────────────────────────────────

type TimelineItem =
  | { kind: 'thread'; data: AdminTicketThread }
  | { kind: 'event';  data: TicketEvent };

function buildTimeline(threads: AdminTicketThread[], events: TicketEvent[]): TimelineItem[] {
  const items: TimelineItem[] = [
    ...threads.map((t): TimelineItem => ({ kind: 'thread', data: t })),
    ...events.map((e): TimelineItem => ({ kind: 'event',  data: e })),
  ];
  items.sort((a, b) => {
    const ta = a.data.created_at;
    const tb = b.data.created_at;
    return ta < tb ? -1 : ta > tb ? 1 : 0;
  });
  return items;
}

function eventLabel(event: TicketEvent): string {
  return `${event.actor_name} · ${event.detail} · ${relativeTime(event.created_at)}`;
}

// ─── Assignment panel ─────────────────────────────────────────────────────────

function AssignmentPanel({
  assigned,
  agents,
  onAssign,
  disabled,
}: {
  assigned: string;
  agents: string[];
  onAssign: (agentName?: string) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
      <span className="text-xs text-gray-500 dark:text-gray-400">Assigned to:</span>
      {assigned ? (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-700 text-brand-700 dark:text-brand-400 rounded-full text-xs font-medium">
          <UserCheck size={11} />
          {assigned}
        </span>
      ) : (
        <span className="text-xs text-gray-400 dark:text-gray-500 italic">Unassigned</span>
      )}

      {/* Claim button */}
      <button
        onClick={() => onAssign(undefined)}
        disabled={disabled}
        className="text-xs px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 disabled:opacity-40 transition-colors"
      >
        Claim
      </button>

      {/* Assign to another dropdown */}
      <div ref={ref} className="relative">
        <button
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 disabled:opacity-40 transition-colors"
        >
          Assign to <ChevronDown size={11} />
        </button>
        {open && (
          <div className="absolute left-0 top-full mt-1 z-20 w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1 text-sm">
            {agents.length > 0 ? (
              agents.map((name) => (
                <button
                  key={name}
                  onClick={() => { onAssign(name); setOpen(false); }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 truncate"
                >
                  {name}
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-400 dark:text-gray-500 text-xs">No agents yet</div>
            )}
          </div>
        )}
      </div>

      {/* Unassign */}
      {assigned && (
        <button
          onClick={() => onAssign('')}
          disabled={disabled}
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-red-100 dark:border-red-800 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-40 transition-colors"
        >
          <UserX size={11} /> Unassign
        </button>
      )}
    </div>
  );
}

// ─── Media lightbox ───────────────────────────────────────────────────────────

function MediaLightbox({ att, blobUrl, onClose }: {
  att: AttachmentMeta;
  blobUrl: string;
  onClose: () => void;
}) {
  const isVideo = att.mime_type.startsWith('video/');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleDownload() {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = att.file_name;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
    >
      <div
        className="relative flex flex-col items-center"
        style={{ maxWidth: '90vw', maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/60 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        {isVideo ? (
          <video
            src={blobUrl}
            controls
            autoPlay
            className="rounded-lg"
            style={{ maxWidth: '85vw', maxHeight: '78vh' }}
          />
        ) : (
          <img
            src={blobUrl}
            alt={att.file_name}
            className="rounded-lg object-contain"
            style={{ maxWidth: '85vw', maxHeight: '78vh' }}
          />
        )}

        <div className="flex items-center justify-between gap-6 mt-4 w-full px-1">
          <span className="text-white/60 text-sm truncate">{att.file_name}</span>
          <button
            onClick={handleDownload}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.25)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
          >
            <Download size={14} /> Download
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Attachment thumbnail ─────────────────────────────────────────────────────

function AttachmentPreview({ att }: { att: AttachmentMeta }) {
  const isImage = att.mime_type.startsWith('image/');
  const isVideo = att.mime_type.startsWith('video/');
  const isMedia = isImage || isVideo;

  const [thumbSrc, setThumbSrc]   = useState<string | null>(null);
  const [lightbox, setLightbox]   = useState(false);
  const [blobUrl, setBlobUrl]     = useState<string | null>(null);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    if (!isImage) return;
    let url = '';
    api.fetchBlob(`/api/admin/attachments/${att.id}`)
      .then((blob) => {
        if (blob) { url = URL.createObjectURL(blob); setThumbSrc(url); }
      })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [att.id, isImage]);

  async function openLightbox() {
    if (!isMedia) return;
    setLightbox(true);
    if (thumbSrc) { setBlobUrl(thumbSrc); return; }
    setLoading(true);
    const blob = await api.fetchBlob(`/api/admin/attachments/${att.id}`).catch(() => null);
    if (blob) setBlobUrl(URL.createObjectURL(blob));
    setLoading(false);
  }

  function closeLightbox() {
    setLightbox(false);
    if (blobUrl && blobUrl !== thumbSrc) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
  }

  async function handleDownload() {
    const blob = await api.fetchBlob(`/api/admin/attachments/${att.id}`).catch(() => null);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = att.file_name;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (isMedia) {
    return (
      <>
        <div
          className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:border-brand-400 dark:hover:border-brand-500 transition-colors cursor-pointer"
          style={{ maxWidth: '220px' }}
          onClick={openLightbox}
          title="Click to preview"
        >
          {isImage ? (
            thumbSrc
              ? <img src={thumbSrc} alt={att.file_name} className="w-full object-cover" style={{ maxHeight: '140px' }} />
              : <div className="w-full bg-gray-50 dark:bg-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500 text-xs" style={{ height: '80px' }}>Loading…</div>
          ) : (
            <div className="w-full bg-gray-800 flex items-center justify-center" style={{ height: '80px' }}>
              <Play size={28} className="text-white/70" />
            </div>
          )}
          <div className="flex items-center justify-between px-2 py-1.5 gap-2 bg-white dark:bg-gray-800">
            <span className="text-xs text-gray-400 dark:text-gray-500 truncate">{att.file_name}</span>
            <button
              onClick={(e) => { e.stopPropagation(); handleDownload(); }}
              className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              title="Download"
            >
              <Download size={13} />
            </button>
          </div>
        </div>

        {lightbox && (
          loading || !blobUrl
            ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={closeLightbox}>
                <div className="text-white text-sm">Loading…</div>
              </div>
            ) : (
              <MediaLightbox att={att} blobUrl={blobUrl} onClose={closeLightbox} />
            )
        )}
      </>
    );
  }

  return (
    <button
      onClick={handleDownload}
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-xs text-gray-700 dark:text-gray-300 transition-colors"
    >
      <Paperclip size={12} className="flex-shrink-0" />
      <span className="truncate" style={{ maxWidth: '180px' }}>{att.file_name}</span>
      <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">{formatBytes(att.file_size)}</span>
    </button>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [ticket, setTicket]   = useState<AdminTicket | null>(null);
  const [threads, setThreads] = useState<AdminTicketThread[]>([]);
  const [events, setEvents]   = useState<TicketEvent[]>([]);
  const [agents, setAgents]   = useState<string[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const [reply, setReply]           = useState('');
  const [replyFiles, setReplyFiles] = useState<ReplyAttachment[]>([]);
  const [sending, setSending]       = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const fileInputRef                = useRef<HTMLInputElement>(null);

  const [statusChanging, setStatusChanging]     = useState(false);
  const [priorityChanging, setPriorityChanging] = useState(false);
  const [productChanging, setProductChanging]   = useState(false);
  const [assignChanging, setAssignChanging]     = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  async function loadDetail() {
    if (!id) return;
    setError(null);
    try {
      const res = await api.getAdminTicket(Number(id));
      setTicket(res.ticket);
      setThreads(res.threads);
      setEvents(res.events ?? []);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to load ticket.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDetail();
    api.getAgents().then(setAgents).catch(() => {});
    api.listProducts().then(setProducts).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [threads, events]);

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    for (const file of files) {
      if (!ALLOWED_MIME[file.type]) {
        setReplyError(`"${file.name}" is not supported.`);
        continue;
      }
      if (file.size > 50 * 1024 * 1024) {
        setReplyError(
          `"${file.name}" is too large (max 50 MB). Your message has been noted — the support team will be in touch to help you share the file.`
        );
        continue;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        setReplyFiles((prev) => [...prev, { name: file.name, type: file.type, data: base64 }]);
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeReplyFile(idx: number) {
    setReplyFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function sendReply(e: FormEvent) {
    e.preventDefault();
    const text = reply.trim();
    if (!text || sending || !ticket) return;
    setSending(true);
    setReplyError(null);
    try {
      const res = await api.adminReplyTicket(
        ticket.id,
        text,
        replyFiles.length > 0 ? replyFiles : undefined,
      );
      setReply('');
      setReplyFiles([]);
      setThreads((prev) => [...prev, {
        id: res.reply_id,
        ticket_id: ticket.id,
        created_by: 'agent',
        author_name: 'You',
        body: text,
        created_at: res.created_at,
        attachments: [],
      }]);
      loadDetail();
    } catch (e) {
      setReplyError(e instanceof ApiClientError ? e.message : 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  }

  async function changeStatus(newStatus: TicketStatus) {
    if (!ticket || statusChanging) return;
    setStatusChanging(true);
    try {
      await api.setTicketStatus(ticket.id, newStatus);
      setTicket((t) => t ? { ...t, status: newStatus } : t);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to update status.');
    } finally {
      setStatusChanging(false);
    }
  }

  async function changePriority(newPriority: TicketPriority) {
    if (!ticket || priorityChanging) return;
    setPriorityChanging(true);
    try {
      await api.setTicketPriority(ticket.id, newPriority);
      setTicket((t) => t ? { ...t, priority: newPriority } : t);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to update priority.');
    } finally {
      setPriorityChanging(false);
    }
  }

  async function changeProduct(productId: number | null) {
    if (!ticket || productChanging) return;
    setProductChanging(true);
    try {
      const res = await api.setTicketProduct(ticket.id, productId);
      setTicket((t) => t ? { ...t, product_id: res.product_id, product_name: res.product_name ?? undefined } : t);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to update product.');
    } finally {
      setProductChanging(false);
    }
  }

  async function handleAssign(agentName?: string) {
    if (!ticket || assignChanging) return;
    setAssignChanging(true);
    try {
      const res = await api.assignTicket(ticket.id, agentName);
      setTicket((t) => t ? { ...t, assigned_to_name: res.assigned_to_name } : t);
      await loadDetail();
      api.getAgents().then(setAgents).catch(() => {});
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to assign ticket.');
    } finally {
      setAssignChanging(false);
    }
  }

  // ── Loading / error states ──

  if (loading) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 py-16 text-center">Loading ticket…</div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="max-w-3xl">
        <button onClick={() => navigate('/tickets')} className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6">
          <ArrowLeft size={14} /> Back to tickets
        </button>
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-800 dark:text-red-400 text-sm">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  const isClosed = ticket.status === 'closed' || ticket.status === 'resolved';
  const timeline = buildTimeline(threads, events);

  return (
    <div className="w-full max-w-3xl flex flex-col md:h-[calc(100vh-64px)]">

      {/* ── Header ── */}
      <div className="flex-shrink-0 mb-4">
        <button
          onClick={() => navigate('/tickets')}
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 transition-colors"
        >
          <ArrowLeft size={14} /> Back to tickets
        </button>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white break-words line-clamp-3">{ticket.subject}</h2>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mt-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="font-mono">#{ticket.id}</span>
                {ticket.source === 'email' && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                    <Mail size={9} />Email
                  </span>
                )}
                {/* Product selector */}
                <span className="inline-flex items-center gap-1">
                  <Package size={11} className="text-gray-400" />
                  <select
                    value={ticket.product_id ?? 0}
                    onChange={(e) => changeProduct(Number(e.target.value) || null)}
                    disabled={productChanging}
                    className="text-xs border border-gray-200 dark:border-gray-600 rounded px-1.5 py-0.5 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:opacity-50"
                  >
                    <option value={0}>No product</option>
                    {products.filter((p) => p.is_active).map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </span>
                <span className="hidden sm:inline text-gray-300 dark:text-gray-600">·</span>
                <span className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
                  <span className="font-medium text-gray-700 dark:text-gray-200">{ticket.user_name || 'Unknown'}</span>
                  <span className="font-mono text-gray-400 dark:text-gray-500">[{ticket.user_id}]</span>
                  {ticket.user_email && (
                    <a href={`mailto:${ticket.user_email}`} className="text-brand-600 dark:text-brand-400 hover:underline break-all">{ticket.user_email}</a>
                  )}
                </span>
                <span className="hidden sm:inline text-gray-300 dark:text-gray-600">·</span>
                <span>
                  Tenant: <span className="font-medium text-gray-700 dark:text-gray-200">{ticket.tenant_name || ticket.tenant_id}</span>
                </span>
                <span className="hidden sm:inline text-gray-300 dark:text-gray-600">·</span>
                <span>Opened {relativeTime(ticket.created_at)}</span>
              </div>
            </div>

            {/* Status + Priority selectors */}
            <div className="flex flex-row flex-wrap sm:flex-col gap-2 sm:flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[ticket.status] ?? STATUS_BADGE.new}`}>
                  {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                </span>
                <select
                  value={ticket.status}
                  onChange={(e) => changeStatus(e.target.value as TicketStatus)}
                  disabled={statusChanging}
                  className="text-xs border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-50"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_BADGE[ticket.priority ?? 'high']}`}>
                  {(ticket.priority ?? 'high').charAt(0).toUpperCase() + (ticket.priority ?? 'high').slice(1)}
                </span>
                <select
                  value={ticket.priority ?? 'high'}
                  onChange={(e) => changePriority(e.target.value as TicketPriority)}
                  disabled={priorityChanging}
                  className="text-xs border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-50"
                >
                  {PRIORITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Assignment panel */}
          <AssignmentPanel
            assigned={ticket.assigned_to_name ?? ''}
            agents={agents}
            onAssign={handleAssign}
            disabled={assignChanging}
          />

          {error && (
            <div className="flex items-center gap-2 mt-3 text-xs text-red-600 dark:text-red-400">
              <AlertCircle size={13} /> {error}
            </div>
          )}
        </div>
      </div>

      {/* ── Thread + event timeline ── */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4 mb-4"
      >
        {timeline.length === 0 && (
          <div className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">No messages yet.</div>
        )}

        {timeline.map((item, idx) => {
          if (item.kind === 'event') {
            return (
              <div key={`ev-${item.data.id}`} className="flex items-center gap-3 py-1">
                <div className="flex-1 border-t border-gray-100 dark:border-gray-700" />
                <span className="text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                  {eventLabel(item.data)}
                </span>
                <div className="flex-1 border-t border-gray-100 dark:border-gray-700" />
              </div>
            );
          }

          const t = item.data;
          const isAgent  = t.created_by === 'agent';
          const isHtml   = !isAgent && looksLikeHtml(t.body);
          const plainText = isHtml ? null : (t.body.includes('<') ? stripHtml(t.body) : t.body);

          return (
            <div key={`th-${t.id ?? idx}`} className={`flex gap-3 ${isAgent ? 'flex-row-reverse' : ''}`}>
              {/* Avatar */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white ${isAgent ? 'bg-brand-600' : 'bg-gray-400 dark:bg-gray-500'}`}>
                {isAgent ? <Headphones size={14} /> : <User size={14} />}
              </div>

              {/* Content area */}
              <div className={`max-w-[90%] sm:max-w-[78%] flex flex-col gap-1 ${isAgent ? 'items-end' : 'items-start'}`}>
                <div className="text-xs text-gray-400 dark:text-gray-500">
                  {t.author_name || (isAgent ? 'Support Agent' : 'Customer')} · {relativeTime(t.created_at)}
                </div>

                {isHtml ? (
                  /* ── HTML e-mail preview (white card, email CSS preserved) ── */
                  <div className="w-full rounded-2xl rounded-tl-sm overflow-hidden border border-gray-200 dark:border-gray-600 bg-white shadow-sm">
                    <HtmlEmailBody html={t.body} />
                  </div>
                ) : (
                  /* ── Plain-text chat bubble ── */
                  <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                    isAgent
                      ? 'bg-brand-600 text-white rounded-tr-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-tl-sm'
                  }`}>
                    {plainText}
                  </div>
                )}

                {/* Attachments */}
                {t.attachments && t.attachments.length > 0 && (
                  <div className={`flex flex-col gap-2 mt-1 ${isAgent ? 'items-end' : 'items-start'}`}>
                    {t.attachments.map((att) => (
                      <AttachmentPreview key={att.id} att={att} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Reply form ── */}
      {isClosed ? (
        <div className="flex-shrink-0 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl px-5 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
          This ticket is {ticket.status} — reopen it to send a reply.
        </div>
      ) : (
        <form onSubmit={sendReply} className="flex-shrink-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          {replyError && (
            <div className="flex items-center gap-2 mb-3 text-xs text-red-600 dark:text-red-400">
              <AlertCircle size={13} /> {replyError}
            </div>
          )}

          {/* Pending attachment chips */}
          {replyFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {replyFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-700 dark:text-gray-300">
                  <Paperclip size={11} />
                  <span className="max-w-[140px] truncate">{f.name}</span>
                  <button type="button" onClick={() => removeReplyFile(i)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 sm:gap-3 items-end">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Attach file"
              className="flex-shrink-0 w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <Paperclip size={14} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.xls,.xlsx,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
            />

            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendReply(e as unknown as FormEvent);
                }
              }}
              disabled={sending}
              placeholder="Type your reply… (Enter to send, Shift+Enter for newline)"
              rows={3}
              className="flex-1 resize-none rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={14} />
              <span>{sending ? 'Sending…' : 'Send'}</span>
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
